/**
 * My API Keys View - For regular users
 * Allows users to view, create, and manage their personal API keys
 */

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Key,
  Plus,
  Copy,
  Eye,
  EyeOff,
  Trash2,
  Check,
  AlertCircle,
  Calendar,
  Cpu,
  Globe,
  Shield,
} from "lucide-react";
import { useDashboardStore } from "@/store";
import { cn } from "@/lib/utils";
import { apiKeysService } from "@/services/apiKeysService";
import type { APIKeyPermission } from "@/types";

interface ApiKey {
  id: string;
  name: string;
  key: string;
  keyPreview: string;
  scopes: string[];
  createdAt: string;
  lastUsed?: string;
  expiresAt?: string;
  status: "active" | "revoked" | "expired";
  rateLimit: number;
  callsToday: number;
  totalCalls: number;
}

export function MyApiKeysView() {
  const { addLogEntry } = useDashboardStore();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [selectedScopes, setSelectedScopes] = useState<string[]>(["read"]);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());

  const SCOPE_OPTIONS = [
    { id: "read", label: "Read Access", description: "Read data from your account" },
    { id: "write", label: "Write Access", description: "Modify data in your account" },
    { id: "delete", label: "Delete Access", description: "Delete data from your account" },
    { id: "agi", label: "AGI Access", description: "Access AGI capabilities" },
  ];

  useEffect(() => {
    loadKeys();
  }, []);

  const loadKeys = async () => {
    setIsLoading(true);
    try {
      // Simulate loading user's API keys
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const mockKeys: ApiKey[] = [
        {
          id: "1",
          name: "Production Key",
          key: "sk-proj-bnk_abc123def456ghi789jkl012mno345pqr678stu901vwx234yz",
          keyPreview: "sk-proj-bnk_abc1...234yz",
          scopes: ["read", "write", "agi"],
          createdAt: "2025-01-15T10:30:00Z",
          lastUsed: "2025-12-28T15:45:00Z",
          status: "active",
          rateLimit: 10000,
          callsToday: 1245,
          totalCalls: 124580,
        },
        {
          id: "2",
          name: "Development Key",
          key: "sk-proj-bnk_xyz789uvw456rst123opq890mno123ghi456jkl789rst012u",
          keyPreview: "sk-proj-bnk_xyz7...012u",
          scopes: ["read", "agi"],
          createdAt: "2025-06-01T09:15:00Z",
          lastUsed: "2025-12-29T08:20:00Z",
          status: "active",
          rateLimit: 5000,
          callsToday: 892,
          totalCalls: 45678,
        },
        {
          id: "3",
          name: "Testing Key",
          key: "sk-proj-bnk_lmn456opq123rst789uvw012xyz456abc789def012ghi345jkl",
          keyPreview: "sk-proj-bnk_lmn4...45jkl",
          scopes: ["read"],
          createdAt: "2025-11-20T14:00:00Z",
          status: "revoked",
          rateLimit: 1000,
          callsToday: 0,
          totalCalls: 2341,
        },
      ];
      
      setKeys(mockKeys);
      
    } catch (error) {
      console.error("Failed to load API keys:", error);
      addLogEntry({
        category: "error",
        action: "Load API Keys",
        details: "Failed to load user API keys",
        severity: "error",
        source: "my-api-keys",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const createKey = async () => {
    if (!newKeyName.trim()) return;
    
    try {
      const { key, keyData } = apiKeysService.generateKey({
        name: newKeyName,
        permissions: selectedScopes as APIKeyPermission[],
        rateLimit: selectedScopes.includes("write") ? 10000 : 5000,
      });
      
      const apiKey: ApiKey = {
        id: keyData.id,
        name: keyData.name,
        key: key,
        keyPreview: `${key.slice(0, 15)}...${key.slice(-4)}`,
        scopes: selectedScopes,
        createdAt: keyData.createdAt.toISOString(),
        status: keyData.status,
        rateLimit: keyData.rateLimit,
        callsToday: 0,
        totalCalls: 0,
      };
      
      setKeys([apiKey, ...keys]);
      setShowCreateModal(false);
      setNewKeyName("");
      setSelectedScopes(["read"]);
      
      addLogEntry({
        category: "security",
        action: "API Key Created",
        details: `Created new API key: ${newKeyName}`,
        severity: "info",
        source: "my-api-keys",
      });
      
    } catch (error) {
      console.error("Failed to create API key:", error);
      addLogEntry({
        category: "error",
        action: "API Key Creation Failed",
        details: String(error),
        severity: "error",
        source: "my-api-keys",
      });
    }
  };

  const revokeKey = async (id: string) => {
    try {
      setKeys(keys.map(k => 
        k.id === id ? { ...k, status: "revoked" as const } : k
      ));
      
      addLogEntry({
        category: "security",
        action: "API Key Revoked",
        details: `Revoked API key: ${id}`,
        severity: "warning",
        source: "my-api-keys",
      });
      
    } catch (error) {
      console.error("Failed to revoke key:", error);
    }
  };

  const copyKey = (key: string) => {
    navigator.clipboard.writeText(key);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const toggleKeyVisibility = (id: string) => {
    const newSet = new Set(visibleKeys);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setVisibleKeys(newSet);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <Key className="mx-auto h-12 w-12 animate-pulse text-[hsl(var(--muted-foreground))]" />
          <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">Loading API keys...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">My API Keys</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Manage your API keys for accessing Binetic services</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 rounded-lg bg-[hsl(var(--primary))] px-4 py-2 text-sm font-medium text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary)/.9)] transition-colors cursor-pointer"
        >
          <Plus className="h-4 w-4" />
          Create New Key
        </button>
      </div>

      {/* Usage Summary */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10">
              <Key className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{keys.length}</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Total Keys</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-500/10">
              <Cpu className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {keys.filter(k => k.status === "active").reduce((sum, k) => sum + k.callsToday, 0).toLocaleString()}
              </p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Calls Today</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-500/10">
              <Shield className="h-5 w-5 text-purple-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{keys.filter(k => k.status === "active").length}</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Active Keys</p>
            </div>
          </div>
        </div>
      </div>

      {/* Keys List */}
      <div className="space-y-4">
        {keys.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--card))] p-12 text-center">
            <Key className="h-12 w-12 text-[hsl(var(--muted-foreground))]" />
            <h3 className="mt-4 text-lg font-semibold">No API keys yet</h3>
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">Create your first API key to get started</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 flex items-center gap-2 rounded-lg bg-[hsl(var(--primary))] px-4 py-2 text-sm font-medium text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary)/.9)] transition-colors cursor-pointer"
            >
              <Plus className="h-4 w-4" />
              Create Your First Key
            </button>
          </div>
        ) : (
          keys.map((key, index) => (
            <motion.div
              key={key.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className={cn(
                "rounded-xl border bg-[hsl(var(--card))] p-6",
                key.status === "revoked" 
                  ? "border-[hsl(var(--destructive)/.3)] bg-[hsl(var(--destructive)/.5)]" 
                  : "border-[hsl(var(--border))]"
              )}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold">{key.name}</h3>
                    {key.status === "revoked" && (
                      <span className="flex items-center gap-1 rounded-full bg-red-500/10 px-2 py-0.5 text-xs font-medium text-red-500">
                        <AlertCircle className="h-3 w-3" />
                        Revoked
                      </span>
                    )}
                  </div>
                  
                  <div className="mt-2 flex items-center gap-2">
                    <code className="flex items-center gap-2 rounded-md bg-[hsl(var(--muted))] px-3 py-1.5 text-sm font-mono">
                      {visibleKeys.has(key.id) ? key.key : key.keyPreview}
                    </code>
                    <button
                      onClick={() => toggleKeyVisibility(key.id)}
                      className="rounded p-1 hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer"
                    >
                      {visibleKeys.has(key.id) ? (
                        <EyeOff className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                      ) : (
                        <Eye className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                      )}
                    </button>
                    <button
                      onClick={() => copyKey(key.key)}
                      className="rounded p-1 hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer"
                    >
                      {copiedKey === key.key ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                      )}
                    </button>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <div>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">Created</p>
                      <p className="text-sm">{formatDate(key.createdAt)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">Rate Limit</p>
                      <p className="text-sm">{key.rateLimit.toLocaleString()}/day</p>
                    </div>
                    <div>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">Usage Today</p>
                      <p className="text-sm">{key.callsToday.toLocaleString()} calls</p>
                    </div>
                    <div>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">Total Calls</p>
                      <p className="text-sm">{key.totalCalls.toLocaleString()}</p>
                    </div>
                  </div>

                  <div className="mt-4">
                    <p className="mb-2 text-xs text-[hsl(var(--muted-foreground))]">Scopes</p>
                    <div className="flex flex-wrap gap-2">
                      {key.scopes.map((scope) => (
                        <span
                          key={scope}
                          className="rounded-md bg-[hsl(var(--muted))] px-2 py-1 text-xs font-medium capitalize"
                        >
                          {scope}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {key.status === "active" && (
                  <button
                    onClick={() => revokeKey(key.id)}
                    className="ml-4 flex h-9 w-9 items-center justify-center rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors cursor-pointer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Create Key Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Create New API Key</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="rounded p-1 hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer"
                >
                  <AlertCircle className="h-5 w-5 text-[hsl(var(--muted-foreground))]" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Key Name</label>
                  <input
                    type="text"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    placeholder="e.g., Production Key"
                    className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Permissions (Scopes)</label>
                  <div className="space-y-2">
                    {SCOPE_OPTIONS.map((scope) => (
                      <label
                        key={scope.id}
                        className="flex cursor-pointer items-start gap-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3 hover:bg-[hsl(var(--muted))] transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedScopes.includes(scope.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedScopes([...selectedScopes, scope.id]);
                            } else {
                              setSelectedScopes(selectedScopes.filter(s => s !== scope.id));
                            }
                          }}
                          className="mt-0.5 h-4 w-4 rounded border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[hsl(var(--primary))] focus:ring-2 focus:ring-[hsl(var(--primary))]"
                        />
                        <div>
                          <p className="text-sm font-medium">{scope.label}</p>
                          <p className="text-xs text-[hsl(var(--muted-foreground))]">{scope.description}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-4">
                  <div className="flex gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-500 shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium text-amber-500">Important</p>
                      <p className="text-[hsl(var(--muted-foreground))] mt-1">
                        Keep your API key secret. Don't share it with others or expose it in client-side code.
                        You won't be able to see it again after creation.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 rounded-lg border border-[hsl(var(--border))] py-2.5 text-sm font-medium hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={createKey}
                  disabled={!newKeyName.trim() || selectedScopes.length === 0}
                  className="flex-1 rounded-lg bg-[hsl(var(--primary))] py-2.5 text-sm font-medium text-[hsl(var(--primary-foreground))] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[hsl(var(--primary)/.9)] transition-colors cursor-pointer"
                >
                  Create API Key
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
